import {
  type MarketId,
  RunnerStatusMarketDetails,
  type SingleOpportunity
} from '@ps/isp-web-services-types';
import { isEmpty, noop } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';

import type { MarketPollingData } from '../types/market';

import useMarketPolling from './useMarketPolling';

const mergeOpportunityAndMarketUpdateData = (
  data: MarketPollingData['data'],
  singleOpportunities: SingleOpportunity[]
): MarketPollingData => {
  const mergedData = Object.entries(data).reduce<MarketPollingData['data']>(
    (acc, [marketId, marketUpdate]) => {
      if (!marketUpdate) return acc;

      const singles: SingleOpportunity[] = singleOpportunities.filter(
        (opp) => opp.runnerId.marketId === marketId
      );

      if (!singles.length) {
        return {
          ...acc,
          [marketId]: marketUpdate
        };
      }

      return {
        ...acc,
        [marketId]: {
          ...marketUpdate,
          runners: marketUpdate.runners.map((runner) => {
            const matchingOpportunity = singles.find(
              (single) => runner.runnerId.selectionId === single.runnerId.selectionId
            );
            /**
             * When matchingOpportunity status is RESTRICTED,
             * use the market details runner status, else use market runner status
             */
            const runnerStatus =
              matchingOpportunity &&
              (matchingOpportunity.status === 'RESTRICTED'
                ? RunnerStatusMarketDetails.ACTIVE
                : runner.runnerStatus);

            return matchingOpportunity
              ? {
                  ...runner,
                  runnerStatus,
                  winRunnerOdds: getIsAvailableOpportunity(matchingOpportunity)
                    ? matchingOpportunity.winRunnerOdds
                    : runner.winRunnerOdds,
                  handicap: getIsAvailableOpportunity(matchingOpportunity)
                    ? matchingOpportunity.handicap
                    : runner.handicap
                }
              : runner;
          })
        }
      };
    },
    {}
  );

  /**
   * Always return an empty array for closed as the opportunity does not contain
   * any information about the market status.
   */
  return { data: mergedData, closed: [] };
};

/**
 * This is a proxy for useMarketPolling that also takes into account updates
 * from the betslip opportunities. The opportunities API often returns data that
 * is more up to date than what SCAN returns so we merge the betslip data over
 * the top of the data that we last received from the market polling.
 */

const useMarketUpdates = (
  marketIds: MarketId[],
  callback: (data: MarketPollingData) => void = noop
): void => {
  const marketPollingData = useRef<MarketPollingData>(undefined);
  const singles = useSelector(select.betSlip.singleOpportunities);

  const callbackIfNotEmpty = useCallback(
    (pollingData: MarketPollingData) => {
      const { data, closed } = pollingData;
      if (!isEmpty(data) || !isEmpty(closed)) {
        callback(pollingData);
      }
    },
    [callback]
  );

  useMarketPolling({
    marketIds,
    onMarketDataReceived: (data) => {
      ((marketPollingData.current = data), callbackIfNotEmpty(data));
    }
  });

  useEffect(() => {
    if (marketPollingData.current) {
      const mergedData = mergeOpportunityAndMarketUpdateData(
        marketPollingData.current.data,
        singles
      );

      callbackIfNotEmpty(mergedData);
    }
  }, [callbackIfNotEmpty, singles]);
};

export default useMarketUpdates;
